import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { FiArrowLeft, FiPhone, FiSearch, FiUser } from 'react-icons/fi';
import PublicDataTable from '../components/PublicDataTable';
import PublicFilterDropdown from '../components/PublicFilterDropdown';
import { CheckboxFilter } from '../components/FilterPanel';
import ContactCell from '../components/ContactCell';
import { DirectoryPage, LastUpdated, UnderlineTabs } from '../components/Directory';
import { DEFAULT_SELECTED_STATE } from '../data/overviewData';
import {
  OVERVIEW_STATES,
  STATE_OVERVIEW_LAST_UPDATED,
  canonicalStateName,
  getStateOverview,
  summarizeSpecies,
} from '../data/stateOverviewData';

const formatNumber = (v) => (v != null ? Number(v).toLocaleString('en-NG') : '—');

function formatPopulation(v) {
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)} M`;
  if (v >= 1_000) return `${Math.round(v / 1_000)} K`;
  return String(v);
}

const contactColumn = { key: 'contact', title: 'Contact', divider: true, render: (v) => <ContactCell number={v} /> };

// Status pill for the forms' own status vocabularies (market status,
// facility functional / licensed status, actor status and verification)
const STATUS_COLORS = {
  Active: '#16a34a', Verified: '#16a34a', Licensed: '#16a34a', High: '#16a34a',
  Seasonal: '#d97706', 'Under Maintenance': '#d97706', 'Under Rehabilitation': '#d97706',
  Pending: '#d97706', 'On Leave': '#d97706', Exempt: '#2563eb', Medium: '#d97706',
  Inactive: '#6b7280', Decommissioned: '#6b7280', Unlicensed: '#dc2626', Suspended: '#dc2626', Low: '#dc2626',
};

function StatusPill({ status }) {
  const color = STATUS_COLORS[status] || '#6b7280';
  return (
    <span
      className="inline-flex items-center justify-center whitespace-nowrap rounded-[20px] px-[12px] py-[5px] text-[11px] font-medium"
      style={{ background: `${color}18`, color, border: `1px solid ${color}40` }}
    >
      {status || '—'}
    </span>
  );
}

const statusColumn = (key, title) => ({ key, title, render: (v) => <StatusPill status={v} /> });

// One tab per "Selected State" panel figure on the Overview page. Columns are
// only fields the matching capture form collects. `typeKey` is the field the
// second filter narrows on.
const TABS = [
  {
    // Population Record (DCIC-01)
    key: 'population',
    label: 'Livestock Population',
    dataKey: 'population',
    typeKey: 'species',
    typeLabel: 'Species',
    columns: [
      { key: 'lga',        title: 'LGA' },
      { key: 'species',    title: 'Species' },
      { key: 'breed',      title: 'Breed / Ecotype' },
      { key: 'system',     title: 'Production System' },
      { key: 'purpose',    title: 'Production Purpose' },
      { key: 'population', title: 'Population Count', render: formatNumber },
      { key: 'herds',      title: 'Herds / Flocks', render: formatNumber },
      { key: 'period',     title: 'Reference Period' },
      { key: 'method',     title: 'Count Method' },
      statusColumn('confidence', 'Confidence Level'),
    ],
  },
  {
    // Market Registry (DCIC-02)
    key: 'markets',
    label: 'Livestock Markets',
    dataKey: 'markets',
    typeKey: 'type',
    typeLabel: 'Market Type',
    columns: [
      { key: 'name',        title: 'Market Name' },
      contactColumn,
      { key: 'lga',         title: 'LGA' },
      { key: 'location',    title: 'Location' },
      { key: 'type',        title: 'Market Type' },
      { key: 'schedule',    title: 'Market Schedule' },
      { key: 'established', title: 'Established' },
      statusColumn('status', 'Status'),
    ],
  },
  {
    // Facility Core Registry (DCIC-06)
    key: 'facilities',
    label: 'Registered Livestock Facilities',
    dataKey: 'facilities',
    typeKey: 'type',
    typeLabel: 'Facility Type',
    columns: [
      { key: 'name',      title: 'Facility Name' },
      { key: 'lga',       title: 'LGA' },
      { key: 'type',      title: 'Facility Type' },
      { key: 'ownership', title: 'Ownership' },
      { key: 'capacity',  title: 'Capacity' },
      statusColumn('status', 'Functional Status'),
      statusColumn('licensed', 'Licensed Status'),
    ],
  },
  {
    // Livestock Actor Registry — actor type Veterinarian
    key: 'veterinarians',
    label: 'Registered Veterinarians',
    dataKey: 'veterinarians',
    typeKey: 'specialisation',
    typeLabel: 'Species Specialisation',
    columns: [
      { key: 'name',           title: 'Full Name' },
      contactColumn,
      { key: 'lga',            title: 'LGA' },
      { key: 'ward',           title: 'Ward / Community' },
      { key: 'registration',   title: 'Registration No.' },
      { key: 'specialisation', title: 'Species Specialisation' },
      statusColumn('status', 'Status'),
      statusColumn('verification', 'Verification'),
    ],
  },
];

// Species share bar — starts empty and grows to its share on mount
function ShareBar({ share }) {
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const frame = requestAnimationFrame(() => setWidth(Math.max(2, share)));
    return () => cancelAnimationFrame(frame);
  }, [share]);
  return (
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-[#eef3ec]">
      <div
        className="h-full rounded-full bg-[#21990f] transition-[width] duration-700 ease-out motion-reduce:transition-none"
        style={{ width: `${width}%` }}
      />
    </div>
  );
}

function StatCard({ label, value, isActive, onClick }) {
  const body = (
    <>
      <span className="text-[13px] font-medium text-[#555]">{label}</span>
      <span className="font-quattrocento text-2xl font-bold text-[#0f3a26] lg:text-[28px]">{value}</span>
    </>
  );
  const base = 'flex flex-col gap-2 rounded-2xl border px-5 py-4 text-left transition-colors';
  if (!onClick) return <div className={`${base} border-white bg-[#ebfce9]`}>{body}</div>;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={isActive}
      className={`${base} ${isActive ? 'border-[#21990f] bg-[#ebfce9]' : 'border-[#e8e8e8] bg-white hover:border-[#21990f]'}`}
    >
      {body}
    </button>
  );
}

/**
 * State Livestock Overview — opened from the Overview map's "View more".
 * The chosen state comes from ?state= (so the page survives a refresh and
 * can be shared) or the link's router state, and can be switched in place.
 */
export default function StateOverview() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Return to whichever page opened this one; a direct visit (no in-app
  // history) falls back to the Overview
  const goBack = () => {
    if (location.key !== 'default') navigate(-1);
    else navigate('/public');
  };

  const state = canonicalStateName(searchParams.get('state'))
    ?? canonicalStateName(location.state?.state)
    ?? DEFAULT_SELECTED_STATE;

  const [tabKey, setTabKey] = useState('population');
  const [query, setQuery] = useState('');
  const [lga, setLga] = useState('All LGAs');
  const [types, setTypes] = useState([]);

  const overview = useMemo(() => getStateOverview(state), [state]);
  const { details } = overview;
  const tab = TABS.find((t) => t.key === tabKey);
  const tabRows = overview[tab.dataKey];

  const typeOptions = useMemo(
    () => [...new Set(tabRows.map((r) => r[tab.typeKey]))].sort(),
    [tabRows, tab]
  );

  const rows = useMemo(() => tabRows.filter((r) =>
    (lga === 'All LGAs' || r.lga === lga)
    && (!types.length || types.includes(r[tab.typeKey]))
  ), [tabRows, tab, lga, types]);

  // Species cards follow the LGA filter only, so every species stays
  // clickable while one is selected; the table follows both filters
  const speciesSummary = useMemo(
    () => (tabKey === 'population'
      ? summarizeSpecies(tabRows.filter((r) => lga === 'All LGAs' || r.lga === lga))
      : []),
    [tabKey, tabRows, lga]
  );
  const shownPopulation = tabKey === 'population' ? rows.reduce((a, r) => a + r.population, 0) : 0;

  // Clicking a species card shows just that species in the table; clicking
  // it again (or the only selected card) shows every species
  const toggleSpecies = (species) => {
    setTypes((current) => (current.length === 1 && current[0] === species ? [] : [species]));
  };

  const resetFilters = () => {
    setLga('All LGAs');
    setTypes([]);
  };

  const changeState = (next) => {
    setSearchParams({ state: next }, { replace: true });
    resetFilters();
  };

  const changeTab = (key) => {
    setTabKey(key);
    resetFilters();
  };

  // Population is a breakdown, not a list of records, so its tab has no count
  const tabs = TABS.map((t) => ({
    key: t.key,
    label: t.key === 'population' ? t.label : `${t.label} (${formatNumber(overview[t.dataKey].length)})`,
  }));

  return (
    <DirectoryPage>
      {/* Back + heading + state switcher */}
      <div className="flex flex-col gap-6">
        <button
          type="button"
          onClick={goBack}
          className="flex w-fit items-center gap-2 rounded-full border border-[#e5e5e5] bg-white px-4 py-2 text-[12px] font-medium text-black shadow-sm transition-colors hover:text-[#21990f]"
        >
          <FiArrowLeft className="h-4 w-4" />
          Go Back
        </button>

        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div className="flex flex-col gap-3">
            <p className="font-oswald text-lg font-normal text-black sm:text-[24px]">State Livestock Overview</p>
            <h1 className="font-quattrocento text-2xl font-bold text-black sm:text-3xl lg:text-[32px]">
              Livestock information for <span className="text-[#e5a81d]">{state}</span>.
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <span className="whitespace-nowrap text-[16px] text-[#8e8e8e]">State:</span>
            <PublicFilterDropdown value={state} options={OVERVIEW_STATES} onChange={changeState} ariaLabel="Select state" align="right" />
          </div>
        </div>
      </div>

      {/* Summary — the market / facility / vet cards double as tab shortcuts */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <StatCard label="Livestock Population" value={formatPopulation(details.livestockPopulation)} isActive={tabKey === 'population'} onClick={() => changeTab('population')} />
        <StatCard label="Livestock Markets" value={formatNumber(details.livestockMarkets)} isActive={tabKey === 'markets'} onClick={() => changeTab('markets')} />
        <StatCard label="Registered Livestock Facilities" value={formatNumber(details.registeredFacilities)} isActive={tabKey === 'facilities'} onClick={() => changeTab('facilities')} />
        <StatCard label="Registered Veterinarians" value={formatNumber(details.registeredVeterinarians)} isActive={tabKey === 'veterinarians'} onClick={() => changeTab('veterinarians')} />
        <div className="flex flex-col gap-2 rounded-2xl border border-white bg-[#ebfce9] px-5 py-4">
          <span className="text-[13px] font-medium text-[#555]">State Coordinator</span>
          <span className="flex items-center gap-2 text-[15px] font-semibold text-[#0f3a26]">
            <FiUser className="h-4 w-4 shrink-0" /> {details.coordinator}
          </span>
          <span className="flex items-center gap-2 text-[13px] text-[#0f3a26]">
            <FiPhone className="h-4 w-4 shrink-0" /> {details.coordinatorPhone}
          </span>
        </div>
      </div>

      {/* Search */}
      <div className="flex w-full items-center gap-[10px] rounded-full bg-[rgba(0,0,0,0.05)] px-5 py-[15px]">
        <FiSearch className="h-[15px] w-[15px] shrink-0 text-[#a4a4a4]" />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={`Search ${tab.label.toLowerCase()} in ${state} by name, LGA, type, contact...`}
          aria-label="Search"
          className="min-w-0 flex-1 bg-transparent text-[13px] font-medium text-black outline-none placeholder:text-[#a4a4a4]"
        />
      </div>

      <div className="flex flex-col gap-4">
        <UnderlineTabs
          tabs={tabs}
          active={tabKey}
          onChange={changeTab}
          right={(
            <div className="flex items-center gap-4">
              <PublicFilterDropdown value={lga} options={['All LGAs', ...overview.lgas]} onChange={setLga} ariaLabel="Filter by LGA" align="right" />
              <CheckboxFilter label={tab.typeLabel} options={typeOptions} selected={types} onChange={setTypes} align="right" />
            </div>
          )}
        />

        {tabKey === 'population' ? (
          <div className="flex flex-col gap-3">
            <p className="text-[13px] text-[#8e8e8e]">
              {formatNumber(shownPopulation)} animals
              {shownPopulation !== details.livestockPopulation && ` of ${formatNumber(details.livestockPopulation)}`} in {state}
              {lga !== 'All LGAs' && ` · ${lga} LGA`}
              {types.length ? ` · ${types.join(', ')}` : ' — by species'}
            </p>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
              {speciesSummary.map((s) => {
                const isSelected = types.includes(s.species);
                const isDimmed = types.length > 0 && !isSelected;
                return (
                  <button
                    key={s.species}
                    type="button"
                    onClick={() => toggleSpecies(s.species)}
                    aria-pressed={isSelected}
                    title={isSelected ? 'Show all species' : `Show only ${s.species} in the table`}
                    className={`flex flex-col gap-1 rounded-xl border px-4 py-3 text-left transition-all ${
                      isSelected
                        ? 'border-[#21990f] bg-[#ebfce9] shadow-sm'
                        : 'border-[#e8e8e8] bg-white hover:border-[#21990f]'
                    } ${isDimmed ? 'opacity-60 hover:opacity-100' : ''}`}
                  >
                    <span className="text-[12px] font-medium text-[#555]">{s.species}</span>
                    <span className="text-[17px] font-bold text-[#0f3a26]">{formatPopulation(s.population)}</span>
                    {/* Re-keyed on state / LGA so the bar grows in again on each change */}
                    <ShareBar key={`${state}:${lga}`} share={s.share} />
                  </button>
                );
              })}
            </div>
          </div>
        ) : (
          <p className="text-[13px] text-[#8e8e8e]">
            Showing {formatNumber(rows.length)} of {formatNumber(tabRows.length)} {tab.label.toLowerCase()} in {state}
            {lga !== 'All LGAs' && ` · ${lga} LGA`}
          </p>
        )}

        <PublicDataTable
          key={`${state}:${tabKey}`}
          columns={tab.columns}
          data={rows}
          searchQuery={query}
          pageSize={10}
        />
      </div>

      <LastUpdated date={STATE_OVERVIEW_LAST_UPDATED} />
    </DirectoryPage>
  );
}
